// Accueil Smart Building : rangée de widgets du bandeau (puissance, énergie du jour, parc, alertes,
// CO₂) et carte « Consommation par zone ». Toutes les valeurs viennent des API : énergie
// journalière et horaire par WattelEM, état du parc (overview), alertes actives.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { wattelEMApi, type DailyEnergy, type DeviceOverview } from '../../api/wattelEM';
import { alertsApi, alertTitle, type AlertEvent } from '../../api/alerts';
import type { InstallationData } from '../../api/architecture';
import { Gauge, Ring } from '../ui/Gauge';
import { useI18n } from '../../i18n';
import { displayDefaultName } from '../../i18n/defaultNames';
import { displaySn } from '../../utils/deviceDisplay';
import { formatPower, powerDisplay, niceCeil } from '../../utils/power';

// Même facteur d'émission que le rapport RSE (RapportRSEPage) : estimation.
const CO2_KG_KWH = 0.0569;

const splitSns = (raw: string | null | undefined) => (raw || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
const dayKey = (d: string) => String(d).slice(0, 10);

export interface EnergyToday {
  loaded: boolean;
  /** kWh du jour par numéro de série (minuscules). */
  todayBySn: Record<string, number>;
  todayKwh: number;
  yesterdayKwh: number | null;
  /** kWh par heure sur les 24 dernières heures, tous appareils confondus. */
  hourly: number[];
  peakKw: number | null;
}

/**
 * Énergie du jour, d'hier et des 24 dernières heures pour les WattelEM donnés.
 * `excluded` : SN lus (todayBySn) mais tenus hors des totaux, typiquement les compteurs de zone
 * d'un tableau dont le WattelEM général mesure déjà l'ensemble.
 */
export function useEnergyToday(sns: string[], excluded: string[] = []): EnergyToday {
  const key = [...sns].sort().join(',');
  const excludedKey = [...excluded].sort().join(',');
  const [state, setState] = useState<EnergyToday>({ loaded: false, todayBySn: {}, todayKwh: 0, yesterdayKwh: null, hourly: [], peakKw: null });

  useEffect(() => {
    if (!key) { setState(s => ({ ...s, loaded: true })); return; }
    let cancelled = false;
    const list = key.split(',');
    const skip = new Set(excludedKey.split(',').filter(Boolean));
    Promise.allSettled(list.map(sn => Promise.all([wattelEMApi.getDailyEnergy(sn, 2), wattelEMApi.getHourlyEnergy(sn, 24)])))
      .then(results => {
        if (cancelled) return;
        // « aujourd'hui » = le jour le plus récent renvoyé par le serveur (fuseau de la base)
        const days = new Set<string>();
        results.forEach(r => { if (r.status === 'fulfilled') r.value[0].forEach((d: DailyEnergy) => days.add(dayKey(d.day))); });
        const sorted = [...days].sort();
        const today = sorted[sorted.length - 1];
        const yesterday = sorted.length > 1 ? sorted[sorted.length - 2] : null;
        const todayBySn: Record<string, number> = {};
        let todayKwh = 0, yesterdayKwh = 0, hasYesterday = false, peak: number | null = null;
        const hours = new Map<string, number>();
        results.forEach((r, i) => {
          if (r.status !== 'fulfilled') return;
          const [daily, hourly] = r.value;
          const inTotals = !skip.has(list[i]);
          for (const d of daily) {
            const k = dayKey(d.day);
            const kwh = Math.max(0, Number(d.daily_kwh) || 0);
            if (k === today) { todayBySn[list[i]] = kwh; if (inTotals) todayKwh += kwh; }
            if (inTotals && yesterday && k === yesterday) { yesterdayKwh += kwh; hasYesterday = true; }
          }
          if (!inTotals) return;
          for (const h of hourly) {
            hours.set(String(h.day), (hours.get(String(h.day)) || 0) + Math.max(0, Number(h.daily_kwh) || 0));
            if (dayKey(h.day) === today) peak = Math.max(peak ?? 0, Number(h.max_power_kw) || 0);
          }
        });
        const hourly = [...hours.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
        setState({ loaded: true, todayBySn, todayKwh, yesterdayKwh: hasYesterday ? yesterdayKwh : null, hourly, peakKw: peak });
      });
    return () => { cancelled = true; };
  }, [key, excludedKey]);

  return state;
}

/** WattelEM rattachés aux installations (même règle que les cartes de l'accueil). */
export function installedSns(installations: InstallationData[]): string[] {
  return [...new Set(installations.flatMap(i => i.zones.flatMap(z => splitSns(z.wattelemSn))))];
}

/**
 * Tableaux ayant un WattelEM général : le général (minuscules) et les compteurs de zone qu'il
 * couvre. Ces derniers ne s'ajoutent pas aux totaux (le général mesure déjà l'ensemble).
 */
export function generalCoverage(installations: InstallationData[]): Array<{ general: string; covered: string[] }> {
  return installations.flatMap(i => i.zones
    .filter(z => z.name.startsWith('Tableau') && z.generalWattelemSn)
    .map(z => {
      const general = String(z.generalWattelemSn).trim().toLowerCase();
      return { general, covered: splitSns(z.wattelemSn).filter(s => s !== general) };
    }));
}

/** SN couverts par un général, tous tableaux confondus (pour les totaux d'énergie). */
export function coveredSns(installations: InstallationData[]): string[] {
  return [...new Set(generalCoverage(installations).flatMap(c => c.covered))];
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
  if (values.length < 2) return <div className="h-[56px]" />;
  const max = Math.max(...values, 0.0001);
  const pts = values.map((v, i) => [(i / (values.length - 1)) * 220, 52 - (v / max) * 46]);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 220 56" preserveAspectRatio="none" className="w-full h-[56px]" aria-hidden>
      <defs><linearGradient id="hw-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".5" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <path d={`${line} L220 56 L0 56 Z`} fill="url(#hw-area)" />
      <path d={line} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const card = 'rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-xl p-4 min-w-0';

/** Rangée de widgets du bandeau de l'accueil. */
export function HomeWidgets({ devices, installations, energy, loading }: {
  devices: DeviceOverview[]; installations: InstallationData[]; energy: EnergyToday; loading: boolean;
}) {
  const { t, locale } = useI18n();
  const [alerts, setAlerts] = useState<AlertEvent[] | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    alertsApi.list(24, ctrl.signal).then(r => setAlerts(r.data.filter(a => a.isActive))).catch(() => setAlerts(null));
    return () => ctrl.abort();
  }, []);

  const sns = useMemo(() => new Set(installedSns(installations)), [installations]);
  const fleet = sns.size ? devices.filter(d => sns.has(d.device_sn.toLowerCase())) : devices;
  const online = fleet.filter(d => d.online).length;
  const offline = fleet.length - online;
  // (pas de compteur « non rattachés » : la liste overview n'est pas filtrée par organisation)
  // Un WattelEM général en ligne mesure tout son tableau : ses compteurs de zone ne s'ajoutent pas.
  const onlineBySn = new Map(devices.map(d => [d.device_sn.toLowerCase(), d.online]));
  const coveredLive = new Set(generalCoverage(installations).filter(c => onlineBySn.get(c.general)).flatMap(c => c.covered));
  const powerKw = fleet.filter(d => d.online && !coveredLive.has(d.device_sn.toLowerCase())).reduce((s, d) => s + (Number(d.current?.power_kw) || 0), 0);
  // Unité lisible (W sous 1 kW) ; l'échelle de la jauge suit la plus grande valeur du jour.
  const scaleKw = Math.max(powerKw, energy.peakKw ?? 0) * 1.25;
  const pd = powerDisplay(Math.max(scaleKw, powerKw));
  const factor = pd.unit === 'W' ? 1000 : 1;
  const gaugeMax = niceCeil(Math.max(scaleKw * factor, pd.unit === 'W' ? 10 : 1));
  const nf = (v: number, d = 1) => v.toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d });
  const delta = energy.yesterdayKwh ? (energy.todayKwh - energy.yesterdayKwh) / energy.yesterdayKwh * 100 : null;

  return (
    <div className="grid gap-3 mt-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {/* Puissance instantanée */}
      <div className={`${card} flex flex-col items-center`}>
        <div className="self-stretch flex justify-between text-xs text-white/75">
          <span>{t('home2_power')}</span>
          {energy.peakKw != null && <span className="font-mono text-amber-200">{t('home2_peak', { v: formatPower(energy.peakKw, locale) })}</span>}
        </div>
        <Gauge value={powerKw * factor} max={gaugeMax} unit={pd.unit} label={t('home2_fleet')} color="#fca311" decimals={pd.unit === 'W' ? 0 : 2} locale={locale} dark />
      </div>

      {/* Énergie du jour */}
      <div className={`${card} flex flex-col gap-2`}>
        <div className="flex justify-between text-xs text-white/75">
          <span>{t('home2_energy_today')}</span>
          {delta != null && <span className={`font-semibold ${delta <= 0 ? 'text-emerald-300' : 'text-amber-200'}`}>{t('home2_vs_yesterday', { v: `${delta > 0 ? '+' : ''}${nf(delta, 0)} %` })}</span>}
        </div>
        <div className="text-white">
          {loading || !energy.loaded ? <div className="h-9 w-24 rounded-lg bg-white/20 animate-pulse" /> : (
            <><span className="font-mono tabular-nums text-[32px] font-semibold tracking-tight">{nf(energy.todayKwh, energy.todayKwh < 10 ? 2 : 1)}</span><span className="ml-1 font-semibold text-white/75">kWh</span></>
          )}
        </div>
        <Sparkline values={energy.hourly} color="#93c5fd" />
        <div className="text-[11px] text-white/60">{energy.yesterdayKwh != null ? t('home2_yesterday', { v: nf(energy.yesterdayKwh, 1) }) : t('home2_last24h')}</div>
      </div>

      {/* Parc d'appareils */}
      <div className={`${card} flex items-center gap-4`}>
        <Ring value={online} max={fleet.length || 1} text={`${online}/${fleet.length}`} caption={t('home2_online')} color="#22c55e" size={96} dark />
        <div className="flex flex-col gap-1.5 text-xs text-white min-w-0">
          <span className="text-white/75">{t('home2_fleet_title')}</span>
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" />{t('home2_n_online', { n: online })}</span>
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-400" />{t('home2_n_offline', { n: offline })}</span>
        </div>
      </div>

      {/* Alertes actives */}
      <Link to="/alertes/seuils" className={`${card} flex flex-col gap-2 text-white no-underline ${alerts?.length ? 'border-red-400/40 bg-red-400/10' : ''}`}>
        <div className="flex justify-between text-xs text-white/80"><span>{t('home2_alerts')}</span><span className="font-semibold text-red-100">{t('home2_see')}</span></div>
        <span className="font-mono tabular-nums text-[32px] font-semibold">{alerts ? alerts.length : '—'}</span>
        <div className="flex flex-col gap-1 text-xs">
          {alerts && alerts.length === 0 && <span className="text-emerald-200">{t('home2_no_alert')}</span>}
          {(alerts || []).slice(0, 2).map(a => (
            <span key={a.id} className="flex items-center gap-2 min-w-0">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.severity === 'critical' ? 'bg-red-400' : 'bg-amber-300'}`} />
              <span className="truncate">{alertTitle(a, t)} · {displaySn(a.deviceSn)}</span>
            </span>
          ))}
        </div>
      </Link>

      {/* Émissions de CO₂ du jour (estimation) */}
      <div className={`${card} flex flex-col gap-2`}>
        <span className="text-xs text-white/75">{t('home2_co2')}</span>
        {(() => {
          // sous 1 kg, en grammes : « 1,1 g » plutôt que « 0,00 kg »
          const kg = energy.todayKwh * CO2_KG_KWH;
          const [v, u, dec] = kg < 1 ? [kg * 1000, 'g CO₂', 1] : [kg, 'kg CO₂', 2];
          return <div className="text-white"><span className="font-mono tabular-nums text-[32px] font-semibold tracking-tight">{nf(v as number, dec as number)}</span><span className="ml-1 font-semibold text-white/75">{u}</span></div>;
        })()}
        <div className="text-[11px] text-white/60 leading-snug">{t('home2_co2_estimate', { co2: nf(CO2_KG_KWH, 4) })}</div>
      </div>
    </div>
  );
}

const ZONE_COLORS = ['#1e88e5', '#14b8a6', '#f59e0b', '#8b5cf6', '#ec4899', '#22c55e', '#0ea5e9', '#f43f5e'];

interface ZoneRow { id: number; name: string; inst: string; instId: number; kwh: number | null }

/** Carte « Consommation par zone » : kWh du jour des zones mesurées par un WattelEM. */
export function ZoneConsumption({ installations, energy }: { installations: InstallationData[]; energy: EnergyToday }) {
  const { t, tp, locale } = useI18n();
  const [instFilter, setInstFilter] = useState<number | 'all'>('all');

  // Zones d'une même installation partageant le même WattelEM (ex. « Salle blanche » et « Parking »
  // mesurées ensemble) : une seule ligne, l'énergie du compteur comptée une fois.
  const all: ZoneRow[] = [];
  const bySns = new Map<string, ZoneRow>();
  installations.forEach(inst => inst.zones.filter(z => !z.name.startsWith('Tableau')).forEach(z => {
    const zs = splitSns(z.wattelemSn);
    const name = displayDefaultName(z.name, t, tp);
    const key = zs.length ? `${inst.id}:${[...zs].sort().join(',')}` : '';
    const shared = key ? bySns.get(key) : undefined;
    if (shared) { shared.name = `${shared.name} + ${name}`; return; }
    const row: ZoneRow = { id: z.id, name, inst: inst.name, instId: inst.id, kwh: zs.length ? zs.reduce((s, sn) => s + (energy.todayBySn[sn] || 0), 0) : null };
    all.push(row);
    if (key) bySns.set(key, row);
  }));
  const zones = instFilter === 'all' ? all : all.filter(z => z.instId === instFilter);
  // mesurées d'abord, de la plus consommatrice à la moins ; non mesurées à part
  const measured = zones.filter((z): z is ZoneRow & { kwh: number } => z.kwh != null).sort((a, b) => b.kwh - a.kwh);
  const unmeasured = zones.filter(z => z.kwh == null);
  // Installations avec un WattelEM général (zone « Tableau ») : le total du jour est sa mesure,
  // et l'écart avec la somme des zones devient « Autres / non mesuré ».
  const generalSnsOf = (inst: InstallationData) => inst.zones
    .filter(z => z.name.startsWith('Tableau') && z.generalWattelemSn)
    .map(z => String(z.generalWattelemSn).trim().toLowerCase());
  const visibleInsts = instFilter === 'all' ? installations : installations.filter(i => i.id === instFilter);
  const withGeneral = visibleInsts.filter(i => generalSnsOf(i).length > 0);
  const generalKwh = withGeneral.reduce((s, i) => s + generalSnsOf(i).reduce((a, sn) => a + (energy.todayBySn[sn] || 0), 0), 0);
  const zonesUnderGeneral = measured.filter(z => withGeneral.some(i => i.id === z.instId)).reduce((s, z) => s + z.kwh, 0);
  const OTHERS_ID = -1;
  const others = withGeneral.length ? Math.max(0, generalKwh - zonesUnderGeneral) : 0;
  const total = measured.reduce((s, z) => s + z.kwh, 0) + others;
  const color = (id: number) => id === OTHERS_ID ? '#cbd5e1' : ZONE_COLORS[Math.max(0, all.findIndex(z => z.id === id)) % ZONE_COLORS.length];
  const nf = (v: number) => v.toLocaleString(locale, { maximumFractionDigits: v === 0 ? 0 : v < 10 ? 2 : 1 });
  const showInst = installations.length > 1 && instFilter === 'all';

  // Anneau : segments espacés, uniquement pour les zones qui consomment (+ « Autres » si général)
  const R = 52, C = 2 * Math.PI * R, GAP = 3;
  const slices: Array<{ id: number; kwh: number }> = total > 0 ? [...measured.filter(z => z.kwh > 0), ...(others > 0 ? [{ id: OTHERS_ID, kwh: others }] : [])] : [];
  let offset = 0;

  return (
    <section className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      {/* En-tête : titre, filtre par installation, total du jour */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 sm:px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 12a9 9 0 1 1-9-9v9h9Z" /><path d="M15 3.5A9 9 0 0 1 20.5 9H15V3.5Z" /></svg>
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-zce-navy font-heading">{t('home2_zone_title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{withGeneral.length ? t('home2_zone_sub_general') : t('home2_zone_sub')}</p>
          </div>
        </div>
        {installations.length > 1 && (
          <div role="tablist" aria-label={t('home2_zone_filter')} className="flex flex-wrap gap-1 p-1 rounded-xl bg-gray-100">
            {[{ id: 'all' as const, name: t('home2_zone_all') }, ...installations.map(i => ({ id: i.id, name: i.name }))].map(o => (
              <button key={o.id} type="button" role="tab" aria-selected={instFilter === o.id} onClick={() => setInstFilter(o.id)}
                className={`h-8 px-3 rounded-lg text-[13px] font-semibold transition-colors ${instFilter === o.id ? 'bg-white text-zce-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {o.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {!energy.loaded ? (
        <div className="p-6"><div className="h-40 rounded-xl bg-gray-100 animate-pulse" /></div>
      ) : zones.length === 0 ? (
        <p className="p-6 text-sm text-gray-500">{t('home2_zone_none')}</p>
      ) : (
        <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-[200px_minmax(0,1fr)] items-center">
          {/* Anneau + total au centre */}
          <div className="relative w-[180px] h-[180px] mx-auto" role="img" aria-label={`${t('home2_zone_total')} : ${nf(total)} kWh`}>
            <svg width="180" height="180" viewBox="0 0 120 120" className="-rotate-90" aria-hidden>
              <circle cx="60" cy="60" r={R} fill="none" stroke="#eef2f7" strokeWidth={10} />
              {slices.map(z => {
                const len = (z.kwh / total) * C;
                const seg = slices.length > 1 ? Math.max(len - GAP, 0.5) : len;
                const el = <circle key={z.id} cx="60" cy="60" r={R} fill="none" stroke={color(z.id)} strokeWidth={10} strokeDasharray={`${seg.toFixed(2)} ${C.toFixed(2)}`} strokeDashoffset={(-offset).toFixed(2)} />;
                offset += len;
                return el;
              })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('home2_zone_total')}</span>
              <span className="font-mono tabular-nums text-[26px] font-semibold text-zce-navy leading-tight">{nf(total)}</span>
              <span className="text-xs font-semibold text-gray-500">kWh</span>
            </div>
          </div>

          {/* Liste des zones */}
          <div className="min-w-0">
            {total === 0 && (
              <div className="mb-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">{t('home2_zone_empty')}</div>
            )}
            <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_minmax(80px,180px)_52px_88px] gap-4 px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              <span>{t('home2_zone_col_zone')}</span><span>{t('home2_zone_col_share')}</span><span className="text-right">%</span><span className="text-right">kWh</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {measured.map(z => {
                const pct = total > 0 ? (z.kwh / total) * 100 : 0;
                const zero = z.kwh === 0;
                return (
                  <li key={z.id} className={`grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_minmax(80px,180px)_52px_88px] gap-x-4 gap-y-1.5 items-center px-3 py-2.5 rounded-lg ${zero ? 'opacity-55' : 'hover:bg-gray-50'}`}>
                    <span className="flex items-center gap-2.5 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: zero ? '#cbd5e1' : color(z.id) }} />
                      <span className="font-medium text-gray-800 truncate">{z.name}</span>
                      {showInst && <span className="shrink-0 h-5 px-2 rounded-md bg-gray-100 text-gray-500 text-[11px] font-medium inline-flex items-center">{z.inst}</span>}
                    </span>
                    <span className="order-last col-span-2 sm:order-none sm:col-span-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color(z.id) }} />
                    </span>
                    <span className="hidden sm:block text-right font-mono tabular-nums text-[13px] text-gray-500">{total > 0 ? `${Math.round(pct)} %` : '—'}</span>
                    <span className="text-right font-mono tabular-nums text-sm font-semibold text-gray-800">{nf(z.kwh)}<span className="sm:hidden text-gray-400 font-normal"> kWh</span></span>
                  </li>
                );
              })}
              {withGeneral.length > 0 && (() => {
                const pct = total > 0 ? (others / total) * 100 : 0;
                return (
                  <li className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_minmax(80px,180px)_52px_88px] gap-x-4 gap-y-1.5 items-center px-3 py-2.5 rounded-lg border border-dashed border-gray-200 bg-gray-50/60" title={t('home2_zone_others_hint')}>
                    <span className="flex items-center gap-2.5 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color(OTHERS_ID) }} />
                      <span className="font-medium text-gray-600 truncate">{t('home2_zone_others')}</span>
                    </span>
                    <span className="order-last col-span-2 sm:order-none sm:col-span-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: '#94a3b8' }} />
                    </span>
                    <span className="hidden sm:block text-right font-mono tabular-nums text-[13px] text-gray-500">{total > 0 ? `${Math.round(pct)} %` : '—'}</span>
                    <span className="text-right font-mono tabular-nums text-sm font-semibold text-gray-600">{nf(others)}<span className="sm:hidden text-gray-400 font-normal"> kWh</span></span>
                  </li>
                );
              })()}
            </ul>
            {unmeasured.length > 0 && (
              <p className="mt-3 px-3 text-xs text-gray-400">{t('home2_zone_unmeasured_note', { n: unmeasured.length, names: unmeasured.map(z => z.name).join(', ') })}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
